import { useCallback, useEffect, useRef, useState } from 'react';
import { faLock } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';
import axios from 'axios';
import { toast } from 'sonner';

import { PaymentMethodSelector } from '@/components/billing/payment-method-selector';
import { StandardAlert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { MutedText, Paragraph } from '@/components/ui/typography';
import { VisuallyHidden } from '@/components/ui/visually-hidden';
import { useSelectedPaymentMethod } from '@/hooks/billing/use-selected-payment-method';
import { NOT_ALLOWED_PAYMENT_TYPES } from '@/lib/billing/payment-method-capabilities';
import { formatMoney } from '@/lib/formatters';
import http, { extractApiErrorMessage } from '@/lib/http';
import {
  cmsBillingAiCreditPacksPath,
  cmsBillingAiCreditPurchasesPath,
  cmsBillingPaymentMethodsIndexPath,
  previewCmsBillingAiCreditPackPath,
} from '@/lib/routes';
import { openWindow, randomId } from '@/lib/utils';
import type {
  AiCreditPack,
  AiCreditPurchasePreview,
  PaymentMethodsResponse,
} from '@/pages/cms/billing/types';

interface AiCreditPacksResponse {
  packs: AiCreditPack[];
  catalogUnavailable: boolean;
}

interface PaymentMethodsJsonResponse extends PaymentMethodsResponse {
  stripePublicKey: string;
}

interface AddCreditsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful purchase, once the dialog has already closed. The caller decides
   * what to refresh (e.g. the billing page reloads its credit ledger); this modal has no opinion
   * on that, since it can be opened from anywhere. */
  onPurchased?: () => void;
}

interface PurchaseResponse {
  status: 'paid' | 'requires_action';
  hostedInvoiceUrl?: string;
}

export default function AddCreditsModal({ open, onOpenChange, onPurchased }: AddCreditsModalProps) {
  const [packs, setPacks] = useState<AiCreditPack[] | null>(null);
  const [packsUnavailable, setPacksUnavailable] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsJsonResponse | null>(null);
  const [selectedPriceId, setSelectedPriceId] = useState('');
  const [isFetchingPacks, setIsFetchingPacks] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [requestKey, setRequestKey] = useState(() => randomId());

  const availablePaymentMethods = paymentMethods?.success ? paymentMethods.data : [];
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useSelectedPaymentMethod({
    paymentMethods: availablePaymentMethods,
    resetOn: open,
    requireImmediatePayment: true,
  });

  const [previewsByPriceId, setPreviewsByPriceId] = useState<
    Record<string, AiCreditPurchasePreview>
  >({});
  const [pendingPreviewIds, setPendingPreviewIds] = useState<Set<string>>(new Set());
  const previewGeneration = useRef(0);

  const fetchPreview = useCallback((pack: AiCreditPack) => {
    const generation = previewGeneration.current;
    const isStale = () => previewGeneration.current !== generation;

    setPendingPreviewIds((current) => new Set(current).add(pack.stripePriceId));
    return http
      .get<AiCreditPurchasePreview>(previewCmsBillingAiCreditPackPath(pack.stripePriceId))
      .then((response) => {
        if (isStale()) return;
        setPreviewsByPriceId((current) => ({ ...current, [pack.stripePriceId]: response.data }));
      })
      .catch(() => {
        if (isStale()) return;
        // The total shown before purchase is only an estimate - the actual invoice (created on
        // Buy Credits) computes tax authoritatively regardless of whether this preview succeeded.
        setPreviewsByPriceId((current) => ({
          ...current,
          [pack.stripePriceId]: {
            subtotalCents: pack.amountCents,
            taxCents: 0,
            totalCents: pack.amountCents,
            currency: pack.currency,
          },
        }));
      })
      .finally(() => {
        if (isStale()) return;
        setPendingPreviewIds((current) => {
          const next = new Set(current);
          next.delete(pack.stripePriceId);
          return next;
        });
      });
  }, []);

  const refreshPreviews = useCallback(() => {
    previewGeneration.current += 1;
    setPreviewsByPriceId({});
    setPendingPreviewIds(new Set());
  }, []);

  useEffect(() => {
    if (!open) return;

    setIsFetchingPacks(true);
    setPermissionDenied(false);
    setPacks(null);
    refreshPreviews();
    http
      .get<AiCreditPacksResponse>(cmsBillingAiCreditPacksPath())
      .then((response) => {
        const loaded = response.data.catalogUnavailable ? [] : response.data.packs;
        setPacksUnavailable(response.data.catalogUnavailable);
        setPacks(loaded);
        setSelectedPriceId((current) =>
          loaded.some((pack) => pack.stripePriceId === current)
            ? current
            : (loaded[0]?.stripePriceId ?? ''),
        );
      })
      .catch((error) => {
        if (axios.isAxiosError(error) && error.response?.status === 403) {
          setPermissionDenied(true);
        }
        setPacksUnavailable(true);
        setPacks([]);
      })
      .finally(() => setIsFetchingPacks(false));
  }, [open, refreshPreviews]);

  const fetchPaymentMethods = useCallback(() => {
    return http
      .get<PaymentMethodsJsonResponse>(cmsBillingPaymentMethodsIndexPath())
      .then((response) => setPaymentMethods(response.data))
      .catch(() =>
        setPaymentMethods({
          success: false,
          data: [],
          canAddPaymentMethod: false,
          paymentMethodLimit: 0,
          stripePublicKey: '',
        }),
      );
  }, []);

  useEffect(() => {
    if (!open || paymentMethods !== null) return;

    fetchPaymentMethods();
  }, [open, paymentMethods, fetchPaymentMethods]);

  // Adding or editing a card can change its billing address, which can change which address
  // Stripe resolves for tax - refresh the cached previews
  // whenever payment methods change, not just on the initial load above.
  const handlePaymentMethodsChanged = useCallback(() => {
    fetchPaymentMethods();
    refreshPreviews();
  }, [fetchPaymentMethods, refreshPreviews]);

  const handlePaymentMethodAdded = (paymentMethodId: string) => {
    handlePaymentMethodsChanged();
    setSelectedPaymentMethodId(paymentMethodId);
  };

  useEffect(() => {
    if (!open) return;

    setPurchaseError(null);
    setRequestKey(randomId());
  }, [open]);

  const selectedPack = packs?.find((pack) => pack.stripePriceId === selectedPriceId);
  const preview = previewsByPriceId[selectedPriceId];
  const isFetchingPreview = pendingPreviewIds.has(selectedPriceId);
  useEffect(() => {
    if (!open || !selectedPack || preview || isFetchingPreview) return;

    fetchPreview(selectedPack);
  }, [open, selectedPack, preview, isFetchingPreview, fetchPreview]);

  const handleBuy = async () => {
    if (!selectedPack) return;

    setSubmitting(true);
    setPurchaseError(null);
    try {
      const response = await http.post<PurchaseResponse>(cmsBillingAiCreditPurchasesPath(), {
        aiCreditPurchase: {
          stripePriceId: selectedPack.stripePriceId,
          requestKey,
          paymentMethodId: selectedPaymentMethodId,
        },
      });

      if (response.data.status === 'requires_action' && response.data.hostedInvoiceUrl) {
        openWindow(response.data.hostedInvoiceUrl, '_blank');
        onOpenChange(false);
        toast.info('Complete your payment in the new tab, then check back for your credits.');
        return;
      }

      onOpenChange(false);
      toast.success(`${selectedPack.credits.toLocaleString()} credits added to your account.`);
      onPurchased?.();
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status !== undefined && status !== 503) {
        setRequestKey(randomId());
      }
      setPurchaseError(extractApiErrorMessage(error) ?? 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (packs === null) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="md">
          <VisuallyHidden>
            <DialogHeader>
              <DialogTitle>Add More Credits</DialogTitle>
              <DialogDescription>
                Credits are added instantly and can be used before your plan renews.
              </DialogDescription>
            </DialogHeader>
          </VisuallyHidden>
          <DialogHeader className="gap-2">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-full" />
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Skeleton className="h-10 w-20" />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (permissionDenied) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="md">
          <DialogHeader className="gap-2">
            <DialogTitle>Add More Credits</DialogTitle>
            <DialogDescription className="text-foreground">
              You don’t have permission to purchase AI credits. Contact your account admin to add
              more.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              colorScheme="secondary"
              onClick={() => onOpenChange(false)}
              autoFocus
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        dismissible={!submitting}
        showCloseButton={!submitting}
        size="lg"
        bodyClassName="space-y-6 p-8"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl leading-8 font-bold">Add More Credits</DialogTitle>
          <DialogDescription className="text-base leading-6">
            Credits are added instantly and can be used before your plan renews.
          </DialogDescription>
        </DialogHeader>

        {packsUnavailable || packs.length === 0 ? (
          <MutedText>AI credit packs are temporarily unavailable. Try again shortly.</MutedText>
        ) : (
          <RadioGroup
            value={selectedPriceId}
            onValueChange={setSelectedPriceId}
            className="gap-2"
            aria-label="Credit pack"
          >
            {packs.map((pack) => {
              const isSelected = pack.stripePriceId === selectedPriceId;
              const optionId = `ai-credits-pack-${pack.stripePriceId}`;

              return (
                <Label
                  key={pack.stripePriceId}
                  htmlFor={optionId}
                  className={`flex cursor-pointer items-center justify-between gap-4 rounded-md px-4 py-5 font-normal ${
                    isSelected ? 'border-2 border-primary' : 'border border-border'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id={optionId} value={pack.stripePriceId} />
                    <Paragraph className="leading-6 font-semibold">{pack.displayName}</Paragraph>
                    {pack.badge ? <Badge size="sm">{pack.badge}</Badge> : null}
                  </div>
                  <Paragraph className="leading-6">
                    {formatMoney(pack.amountCents, pack.currency)}
                  </Paragraph>
                </Label>
              );
            })}
          </RadioGroup>
        )}

        <div className="flex flex-col gap-3">
          <Paragraph className="text-sm leading-5 font-semibold">Payment Method</Paragraph>

          {paymentMethods === null ? (
            <Skeleton className="h-10 w-full" />
          ) : paymentMethods.success && paymentMethods.stripePublicKey ? (
            <PaymentMethodSelector
              paymentMethods={availablePaymentMethods}
              selectedId={selectedPaymentMethodId}
              onSelect={setSelectedPaymentMethodId}
              stripePublicKey={paymentMethods.stripePublicKey}
              googleMapsApiKey={paymentMethods.googleMapsApiKey}
              canAddPaymentMethod={paymentMethods.canAddPaymentMethod ?? false}
              onAdded={handlePaymentMethodAdded}
              onUpdated={handlePaymentMethodsChanged}
              idPrefix="ai-credits-payment-method"
              disabledTypes={NOT_ALLOWED_PAYMENT_TYPES}
            />
          ) : (
            <MutedText>Unable to load payment methods.</MutedText>
          )}
        </div>

        <Separator />

        <div className="flex flex-col gap-1.5">
          {preview && preview.taxCents > 0 ? (
            <div className="flex items-center justify-between">
              <Paragraph className="text-sm leading-5">Tax</Paragraph>
              <Paragraph className="text-sm leading-5">
                {formatMoney(preview.taxCents, preview.currency, false)}
              </Paragraph>
            </div>
          ) : null}
          <div className="flex items-center justify-between">
            <Paragraph className="text-sm leading-5 font-semibold">Total</Paragraph>
            <Paragraph className="text-lg leading-7 font-semibold">
              {preview ? formatMoney(preview.totalCents, preview.currency, false) : null}
            </Paragraph>
          </div>
        </div>

        {purchaseError ? <StandardAlert variant="destructive" description={purchaseError} /> : null}

        <DialogFooter className="items-center justify-between sm:justify-between">
          <MutedText className="flex items-center gap-1 text-xs">
            <Icon icon={faLock} className="size-4" />
            Secure checkout
          </MutedText>
          <div className="flex gap-2">
            <Button
              size="lg"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              size="lg"
              onClick={handleBuy}
              disabled={
                !selectedPack ||
                !selectedPaymentMethodId ||
                submitting ||
                isFetchingPacks ||
                isFetchingPreview ||
                !preview
              }
              isLoading={submitting || isFetchingPacks || isFetchingPreview}
              loadingText={submitting ? 'Processing...' : undefined}
            >
              Buy Credits
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
